import { DatePicker } from 'antd'
import moment from 'moment'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { ActionEnum, getDateFormat, getMinutes } from '../../../common'
import {
  ColumnActionHeader,
  ColumnInputHeader,
  PageTitleComponent,
  TableActionColumnButtonComponent,
  TableComponent,
} from '../../../components'
import { translate } from '../../../languages'
import { WorkScheduleEntity } from '../../../models/workSchedule/workSchedule'
import { RootState } from '../../../stores'
import {
  loadWorkSchedules,
  setAction,
  setSelectedWorkSchedule,
  setShowSearchDrawer,
  setWorkScheduleFilter,
} from '../../../stores/actions'
import {
  ManageWorkScheduleModalComponent,
  SearchWorkScheduleDrawerComponent,
} from './components'

export const WorkSchedulePage: React.FC = () => {
  const dispatch = useDispatch()
  const { language } = useSelector((state: RootState) => state.language)
  const { management } = useSelector((state: RootState) => state.common)
  const { workSchedules, isWorkSchedulesLoaded, workScheduleFilter } =
    useSelector((state: RootState) => state.workSchedule)
  const isDesktopScreen = useMediaQuery({ minWidth: '1300px' })

  const [filter, setFilter] = React.useState(workScheduleFilter)

  React.useEffect(() => {
    if (isDesktopScreen) {
      dispatch(setShowSearchDrawer(false))
    }
    // eslint-disable-next-line
  }, [isDesktopScreen])

  React.useEffect(() => {
    dispatch(loadWorkSchedules())
    // eslint-disable-next-line
  }, [])

  React.useEffect(() => {
    setFilter(workScheduleFilter)
  }, [workScheduleFilter])

  const filteredWorkSchedule = React.useMemo(
    () =>
      workSchedules.filter(
        (workSchedule) =>
          workSchedule.employee.name
            .toLowerCase()
            .includes(workScheduleFilter.employeeName.toLowerCase()) &&
          workSchedule.employee.station.name
            .toLowerCase()
            .includes(workScheduleFilter.stationName.toLowerCase()) &&
          workSchedule.shift.name
            .toLowerCase()
            .includes(workScheduleFilter.shiftName.toLowerCase()) &&
          getMinutes(workSchedule.shift.startTime) >=
            getMinutes(workScheduleFilter.startTime) &&
          getMinutes(workSchedule.shift.endTime) <=
            getMinutes(workScheduleFilter.endTime) &&
          (!workScheduleFilter.startDate ||
            workSchedule.startDate >= workScheduleFilter.startDate) &&
          (!workScheduleFilter.endDate ||
            workSchedule.endDate <= workScheduleFilter.endDate)
      ),
    [workSchedules, workScheduleFilter]
  )

  const columns = React.useMemo(
    () => [
      {
        dataIndex: 'id',
        title: translate('workSchedule.id', language),
        width: 30,
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="workSchedule.employee"
            value={filter.employeeName}
            onChange={(e) =>
              setFilter({ ...filter, employeeName: e.target.value })
            }
          />
        ),
        render: (_: any, workSchedule: WorkScheduleEntity) =>
          workSchedule.employee.name,
        width: 130,
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="workSchedule.station"
            value={filter.stationName}
            onChange={(e) =>
              setFilter({ ...filter, stationName: e.target.value })
            }
          />
        ),
        render: (_: any, workSchedule: WorkScheduleEntity) =>
          workSchedule.employee.station.name,
        width: 130,
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="workSchedule.shift"
            value={filter.shiftName}
            onChange={(e) =>
              setFilter({ ...filter, shiftName: e.target.value })
            }
          />
        ),
        render: (_: any, workSchedule: WorkScheduleEntity) =>
          workSchedule.shift.name,
        width: 130,
      },
      {
        title: (
          <>
            <span>{translate('workSchedule.time', language)}</span>
            {isDesktopScreen && (
              <DatePicker.RangePicker
                picker="time"
                style={{ width: '100%' }}
                format="HH:mm"
                value={[
                  moment(filter.startTime, 'HH:mm'),
                  moment(filter.endTime, 'HH:mm'),
                ]}
                allowClear={false}
                inputReadOnly
                onChange={(_, dateString) =>
                  setFilter({
                    ...filter,
                    startTime:
                      dateString && dateString.length === 2
                        ? dateString[0]
                        : '00:00',
                    endTime:
                      dateString && dateString.length === 2
                        ? dateString[1]
                        : '23:59',
                  })
                }
              />
            )}
          </>
        ),
        render: (_: any, workSchedule: WorkScheduleEntity) =>
          `${workSchedule.shift.startTime} -> ${workSchedule.shift.endTime}`,
        width: 175,
      },
      {
        title: (
          <>
            <span>{translate('workSchedule.date', language)}</span>
            {isDesktopScreen && (
              <DatePicker.RangePicker
                picker="date"
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                value={
                  filter.startDate && filter.endDate
                    ? [moment(filter.startDate), moment(filter.endDate)]
                    : undefined
                }
                allowClear={false}
                onChange={(e) =>
                  setFilter({
                    ...filter,
                    startDate:
                      e && e.length === 2 && e[0]
                        ? e[0]?.toDate().getTime()
                        : 0,
                    endDate:
                      e && e.length === 2 && e[1]
                        ? e[1]?.toDate().getTime()
                        : undefined,
                  })
                }
              />
            )}
          </>
        ),
        render: (_: any, workSchedule: WorkScheduleEntity) =>
          `${getDateFormat(workSchedule.startDate)} -> ${getDateFormat(
            workSchedule.endDate
          )}`,
        width: 175,
      },
      {
        title: (
          <ColumnActionHeader
            onSearch={() => dispatch(setWorkScheduleFilter(filter))}
          />
        ),
        render: (_: any, workSchedule: WorkScheduleEntity) => (
          <TableActionColumnButtonComponent
            onViewButtonClick={() => {
              dispatch(setSelectedWorkSchedule(workSchedule))
              dispatch(setAction(ActionEnum.VIEW))
            }}
            onDeleteButtonClick={() => {
              dispatch(setSelectedWorkSchedule(workSchedule))
              dispatch(setAction(ActionEnum.DELETE))
            }}
          />
        ),
        width: 130,
      },
    ],
    [language, filter, isDesktopScreen, dispatch]
  )

  return (
    <div style={{ height: '97.5%' }}>
      {management.showManageModal && <ManageWorkScheduleModalComponent />}
      <PageTitleComponent
        onCreateButtonClick={() => {
          dispatch(setSelectedWorkSchedule())
          dispatch(setAction(ActionEnum.CREATE))
        }}
        hasSearchDrawer
      />
      {management.showSearchDrawer && <SearchWorkScheduleDrawerComponent />}
      <TableComponent
        data={filteredWorkSchedule}
        columns={columns}
        loading={!isWorkSchedulesLoaded}
      />
    </div>
  )
}
